/**
 * Staffology Design System — StatusPill
 * Version: 2.0.0
 *
 * The single source of truth for status visualization across
 * tables, cards, headers, and detail pages.
 *
 * Usage:
 *   <StatusPill status="active" />
 *   <StatusPill status="pending" label="Filling Order" />
 *   <StatusPill status="custom" tone="warning" label="Custom" />
 *
 * The "status" prop maps to the business state vocabulary
 * defined in Application Guidelines §13. When a status code
 * doesn't match any preset, use status="custom" + a tone.
 */

import React, {type HTMLAttributes} from 'react'
import styled, {css} from 'styled-components'
import {Tooltip} from './BaseComponents'
/* All styling uses CSS custom properties (var(--*)) from colors_and_type.css
   so this component works with or without a styled-components ThemeProvider. */

export type StatusTone =
  | 'success' // Active, Approved, Filled, Order Filled
  | 'warning' // Pending, Filling Order, Awaiting Confirmation
  | 'error' // Failed, Archived (destructive)
  | 'info' // Payroll Sent, Reviewing
  | 'pending' // In progress, Confirming, Preparing Pledges
  | 'neutral' // Submitting, Processing, Draft

/**
 * Preset business statuses. Each maps to a tone and a default
 * label. Override the label via the `label` prop when the same
 * tone applies to a different business state.
 */
export type StatusCode =
  | 'active'
  | 'approved'
  | 'filled'
  | 'order-filled'
  | 'pending'
  | 'filling-order'
  | 'awaiting-confirmation'
  | 'failed'
  | 'archived'
  | 'payroll-sent'
  | 'reviewing'
  | 'confirming'
  | 'preparing-pledges'
  | 'submitting'
  | 'processing'
  | 'draft'
  | 'custom'

const STATUS_MAP: Record<Exclude<StatusCode, 'custom'>, {tone: StatusTone; label: string}> = {
  active: {tone: 'success', label: 'Active'},
  approved: {tone: 'success', label: 'Approved'},
  filled: {tone: 'success', label: 'Filled'},
  'order-filled': {tone: 'success', label: 'Order Filled'},
  pending: {tone: 'warning', label: 'Pending'},
  'filling-order': {tone: 'warning', label: 'Filling Order'},
  'awaiting-confirmation': {tone: 'warning', label: 'Awaiting Confirmation'},
  failed: {tone: 'error', label: 'Failed'},
  archived: {tone: 'error', label: 'Archived'},
  'payroll-sent': {tone: 'info', label: 'Payroll Sent'},
  reviewing: {tone: 'info', label: 'Reviewing'},
  confirming: {tone: 'pending', label: 'Confirming'},
  'preparing-pledges': {tone: 'pending', label: 'Preparing Pledges'},
  submitting: {tone: 'neutral', label: 'Submitting'},
  processing: {tone: 'neutral', label: 'Processing'},
  draft: {tone: 'neutral', label: 'Draft'},
}

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  status: StatusCode
  /** Required when status="custom". Optional override for presets. */
  label?: string
  /** Required when status="custom". Ignored otherwise. */
  tone?: StatusTone
  /** Show the colored dot before the label. Default true. */
  withDot?: boolean
  size?: 'sm' | 'md'
  /** Optional click handler. When provided, the pill renders as role="button". */
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void
  /** Disable the pill (only applies when onClick is provided). */
  disabled?: boolean
  /**
   * Suppress the built-in label Tooltip. Use when a parent already wraps the pill
   * in its own Tooltip (e.g. to show an error message) — otherwise both render and
   * you get two stacked bubbles on hover.
   */
  disableTooltip?: boolean
}

const toneStyles = {
  success: css`
    background: var(--success-100);
    color: var(--success-800);
    --dot-color: var(--success-500);
  `,
  warning: css`
    background: var(--warning-100);
    color: var(--warning-800);
    --dot-color: var(--warning-500);
  `,
  error: css`
    background: var(--error-100);
    color: var(--error-800);
    --dot-color: var(--error-500);
  `,
  info: css`
    background: var(--info-100);
    color: var(--info-800);
    --dot-color: var(--info-500);
  `,
  pending: css`
    background: var(--pending-100);
    color: var(--pending-800);
    --dot-color: var(--pending-500);
  `,
  neutral: css`
    background: var(--neutral-200);
    color: var(--neutral-800);
    --dot-color: var(--neutral-600);
  `,
}

const sizeStyles = {
  sm: css`
    font-size: var(--fs-caption);
    padding: 2px var(--space-2);
    gap: 6px;
  `,
  md: css`
    font-size: var(--fs-body);
    padding: var(--space-1) 10px;
    gap: 6px;
  `,
}

const Pill = styled.span<{
  $tone: StatusTone
  $size: 'sm' | 'md'
  $clickable?: boolean
  $disabled?: boolean
}>`
  display: inline-flex;
  align-items: center;
  border-radius: var(--radius-full);
  font-weight: var(--fw-medium);
  font-family: var(--font-primary);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  min-width: 0;

  ${({$tone}) => toneStyles[$tone]}
  ${({$size}) => sizeStyles[$size]}

  ${({$clickable, $disabled}) =>
    $clickable &&
    css`
      cursor: ${$disabled ? 'not-allowed' : 'pointer'};
      opacity: ${$disabled ? 0.5 : 1};
      transition: filter 0.15s ease;
      border: none;

      &:hover {
        ${!$disabled && 'filter: brightness(0.93);'}
      }
      &:focus-visible {
        outline: 2px solid var(--brand-cta);
        outline-offset: 2px;
      }
    `}
`

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--dot-color);
  flex-shrink: 0;
`

/* The label lives in its own shrinkable span so a long status ("Pledges
   Received") ellipsis-truncates inside a narrow cell instead of overflowing.
   The raw text node couldn't do this — text-overflow doesn't apply to a flex
   container's anonymous text. Only truncates when the pill is width-constrained
   by its container; unconstrained, it sizes to content as before. */
const LabelText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

export const StatusPill = ({
  status,
  label,
  tone,
  withDot = true,
  size = 'sm',
  onClick,
  disabled,
  disableTooltip,
  ...rest
}: StatusPillProps) => {
  let resolvedTone: StatusTone, resolvedLabel: string

  if (status === 'custom') {
    if (!tone || !label) {
      console.warn('StatusPill: status="custom" requires both `tone` and `label` props.')
      resolvedTone = tone ?? 'neutral'
      resolvedLabel = label ?? 'Unknown'
    } else {
      resolvedTone = tone
      resolvedLabel = label
    }
  } else {
    const preset = STATUS_MAP[status]
    if (preset) {
      resolvedTone = preset.tone
      resolvedLabel = label ?? preset.label
    } else {
      // Unknown/undefined status code — fall back to neutral rather than crashing.
      // Use the explicit tone/label if provided, else humanize the raw code.
      resolvedTone = tone ?? 'neutral'
      resolvedLabel = label ?? (status ? String(status) : 'Unknown')
    }
  }

  const isClickable = !!onClick

  const pill = (
    <Pill
      $tone={resolvedTone}
      $size={size}
      $clickable={isClickable}
      $disabled={disabled}
      role={isClickable ? 'button' : 'status'}
      tabIndex={isClickable && !disabled ? 0 : undefined}
      aria-label={`Status: ${resolvedLabel}`}
      aria-disabled={isClickable && disabled ? true : undefined}
      onClick={!disabled ? onClick : undefined}
      onKeyDown={
        isClickable && !disabled
          ? (e: React.KeyboardEvent<HTMLSpanElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.(e as unknown as React.MouseEvent<HTMLSpanElement>)
              }
            }
          : undefined
      }
      {...rest}>
      {withDot && <Dot aria-hidden="true" />}
      <LabelText>{resolvedLabel}</LabelText>
    </Pill>
  )

  // A parent already supplies its own Tooltip — don't add a second one.
  if (disableTooltip) {
    return pill
  }

  // Render the full status label in a DS Tooltip for when the pill truncates on
  // narrow screens. The label repeats the pill's text, so the DS shows it only
  // while the label is cut off (STFFLG-4236). Replaces the native `title`; the
  // Tooltip portals out, escaping the cell's overflow:hidden.
  return <Tooltip content={resolvedLabel}>{pill}</Tooltip>
}
